const API_KEY = '08a35b7a155b45c79a7a45eef44d53d2';

// ── Types ──

export interface Station {
  Code: string;
  Name: string;
  StationTogether1: string;
  StationTogether2: string;
  LineCode1: string | null;
  LineCode2: string | null;
  LineCode3: string | null;
  LineCode4: string | null;
  Lat: number;
  Lon: number;
  Address: {
    Street: string;
    City: string;
    State: string;
    Zip: string;
  };
}

export interface Prediction {
  Car: string | null;
  Destination: string;
  DestinationCode: string | null;
  DestinationName: string;
  Group: string;
  Line: string;
  LocationCode: string;
  LocationName: string;
  Min: string;
}

export interface TrainPosition {
  TrainId: string;
  TrainNumber: string;
  CarCount: number;
  DirectionNum: number;
  CircuitId: number;
  DestinationStationCode: string | null;
  LineCode: string | null;
  SecondsAtLocation: number;
  ServiceType: string;
}

export interface RailIncident {
  IncidentID: string;
  Description: string;
  StartLocationFullName: string | null;
  EndLocationFullName: string | null;
  PassengerDelay: number;
  IncidentType: string;
  EmergencyText: string | null;
  LinesAffected: string;
  DateUpdated: string;
}

// ── Fetch helpers ──

function withKey(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}api_key=${API_KEY}`;
}

async function apiFetch<T>(url: string): Promise<T> {
  const response = await fetch(withKey(url));
  if (!response.ok) {
    throw new Error(`WMATA API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

// ── API Methods ──

export async function fetchStations(): Promise<Station[]> {
  const data = await apiFetch<{ Stations: Station[] }>(
    'https://api.wmata.com/Rail.svc/json/jStations'
  );
  return data.Stations;
}

export async function fetchPredictions(stationCode: string = 'All'): Promise<Prediction[]> {
  const data = await apiFetch<{ Trains: Prediction[] }>(
    `https://api.wmata.com/StationPrediction.svc/json/GetPrediction/${stationCode}`
  );
  return data.Trains;
}

export async function fetchTrainPositions(): Promise<TrainPosition[]> {
  const data = await apiFetch<{ TrainPositions: TrainPosition[] }>(
    'https://api.wmata.com/TrainPositions/TrainPositions?contentType=json'
  );
  return data.TrainPositions.filter(
    (t) => t.ServiceType === 'Normal' && t.LineCode
  );
}

export async function fetchIncidents(): Promise<RailIncident[]> {
  const data = await apiFetch<{ Incidents: RailIncident[] }>(
    'https://api.wmata.com/Incidents.svc/json/RailIncidents'
  );
  return data.Incidents;
}

// ── Helpers ──

export function getStationLineCodes(station: Station): string[] {
  return [station.LineCode1, station.LineCode2, station.LineCode3, station.LineCode4]
    .filter((c): c is string => c !== null && c !== '');
}

export function getPredictionsForStation(
  predictions: Prediction[],
  stationCode: string
): Prediction[] {
  return predictions
    .filter((p) => p.LocationCode === stationCode)
    .sort((a, b) => {
      const aMin = a.Min === 'ARR' ? -1 : a.Min === 'BRD' ? -2 : parseInt(a.Min) || 999;
      const bMin = b.Min === 'ARR' ? -1 : b.Min === 'BRD' ? -2 : parseInt(b.Min) || 999;
      return aMin - bMin;
    });
}
